import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { t } from 'ttag';

import client from 'utils/client';

import { HiBadgeCheck } from 'react-icons/hi';
import { USPList } from 'components';

import Modal from './Modals/Modal';

import MembershipLogo from '../assets/Membership-Logo.svg';

function App({ signupSource, linkToSignup }) {
  const searchParams = new URLSearchParams(window?.location?.search);
  const signupSourceFromParams = searchParams.has('signupSource')
    ? searchParams.get('signupSource')
    : undefined;

  /**
   * Determines whether the Login Modal should be shown immediately, when the component is mounted.
   * @type {[boolean, function(boolean): void]} showLoginModal, toggleLoginModal
   */
  const [showLoginModal, toggleLoginModal] = useState(
    document.querySelector('#account-login')?.dataset?.showLoginModal ===
      'True' || false
  );

  /**
   * Determines whether the Register Modal should be shown immediately, when the component is mounted.
   * @type {[boolean, function(boolean): void]} validateEmail, toggleValidateEmail
   */
  const [validateEmail, toggleValidateEmail] = useState(
    document.querySelector('#account-login')?.dataset?.validateEmail ===
      'True' || false
  );

  /**
   * A list of USPs to be displayed in the USP List.
   * @type {[{headline: string, body: string, icon: string | node}[], function({title: string, description: string}[]): void]} USPs, setUSPs
   */
  const [USPs, setUSPs] = useState([]);

  /**
   * The USP Type used to fetch the USPs from the API.
   * @type {string}
   */
  const USPType = useMemo(() => 'signup_unisport_fc_-_usp', []);

  /**
   * Fetches the USPs from the API.
   * @type {React.useEffect<void>}
   */
  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    client
      .get(`/api/get_usp/?type=${USPType}`, { signal: controller.signal })
      .then(response => {
        if (response.status === 200 && response.data) {
          if (isMounted)
            setUSPs(
              response.data[USPType].map(USP => ({
                ...USP,
                icon: (
                  <HiBadgeCheck className="h-6 w-6 shrink-0 text-[#9D54D8]" />
                )
              }))
            );
        }
      });

    return () => {
      controller.abort();
      isMounted = false;
    };
  }, []);

  /**
   * Disables the Profile Notification in the Header, when the component is mounted.
   * @returns {void}
   */
  useEffect(() => {
    if (window?.localStorage?.getItem('showProfileNotification')) {
      window?.localStorage?.setItem('showProfileNotification', 'False');
    }
  }, []);

  return (
    <>
      <div className="flex flex-col items-center p-4 md:p-8">
        <USPList
          headingText={t`Get Exclusive Member Benefits`}
          USPs={USPs}
          CTA={{
            content: (
              <>
                <img src={MembershipLogo} alt="" className="h-6 w-6" />
                {t`Log in or sign up`}
              </>
            ),
            onClick: () => {
              if (linkToSignup) {
                const currentUrl = window?.location?.href || undefined;
                window.location.href = `/account/login/?show_modal=True${
                  currentUrl ? `&next=${currentUrl}&signupSource=campaign` : ''
                }`;
              } else {
                toggleLoginModal(true);
              }
            }
          }}
          maxWidth="md"
        />
      </div>
      <Sentry.ErrorBoundary fallback={null}>
        <Modal
          isOpen={showLoginModal || validateEmail}
          onClose={() => {
            toggleLoginModal(false);
            toggleValidateEmail(false);
          }}
          modal={validateEmail ? 'validate' : 'login'}
          loginHeading={t`Log in or become a member`}
          signupSource={
            signupSourceFromParams === undefined
              ? signupSource
              : signupSourceFromParams
          }
        />
      </Sentry.ErrorBoundary>
    </>
  );
}

App.propTypes = {
  signupSource: PropTypes.string,
  linkToSignup: PropTypes.bool
};

App.defaultProps = {
  signupSource: 'profile',
  linkToSignup: false
};

export default App;
